<template>
    <div class="relative w-full max-w-full min-w-0 overflow-hidden group/container">


        <!-- Scroll Button Left -->
        <button v-if="showLeftFade" 
                @click="scrollLeft"
                class="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center z-20 hover:bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 transition-all opacity-0 group-hover/container:opacity-100 hidden md:flex">
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
        </button>

        <!-- Scroll Container -->
        <div ref="scrollContainer"
             @scroll="checkScroll"
             class="flex overflow-x-auto gap-4 pb-4 px-1 scroll-smooth snap-x snap-mandatory no-scrollbar"
             style="-webkit-overflow-scrolling: touch;">
            <slot></slot>
        </div>



        <!-- Scroll Button Right -->
        <button v-if="showRightFade" 
                @click="scrollRight"
                class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center z-20 hover:bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 transition-all opacity-0 group-hover/container:opacity-100 hidden md:flex">
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
        </button>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, onUpdated, defineProps, defineEmits } from 'vue'

const props = defineProps({
    hasMore: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['load-more'])

const scrollContainer = ref(null)
const showLeftFade = ref(false)
const showRightFade = ref(true)

const checkScroll = () => {
    if (!scrollContainer.value) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value
    showLeftFade.value = scrollLeft > 10
    showRightFade.value = scrollLeft < scrollWidth - clientWidth - 10 || props.hasMore

    // Trigger load more if we have more and are near the end (100px threshold)
    if (props.hasMore && scrollLeft >= scrollWidth - clientWidth - 100) {
        emit('load-more')
    }
}

const scrollLeft = () => {
    if (!scrollContainer.value) return
    scrollContainer.value.scrollBy({ left: -300, behavior: 'smooth' })
}

const scrollRight = () => {
    if (!scrollContainer.value) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value
    
    // If we're already near the end and we have more items to load, emit load-more on click
    if (props.hasMore && scrollLeft >= scrollWidth - clientWidth - 50) {
        emit('load-more')
    } else {
        scrollContainer.value.scrollBy({ left: 300, behavior: 'smooth' })
    }
}

onUpdated(() => {
    checkScroll()
})

let resizeObserver = null

onMounted(() => {
    checkScroll() // Initial check
    
    // Check on resize
    if (window.ResizeObserver && scrollContainer.value) {
        resizeObserver = new ResizeObserver(checkScroll)
        resizeObserver.observe(scrollContainer.value)
    }
    
    window.addEventListener('resize', checkScroll)
})

onUnmounted(() => {
    if (resizeObserver) resizeObserver.disconnect()
    window.removeEventListener('resize', checkScroll)
})
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}
</style>
